import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ROUTES } from "../../../../app/config/routes";
import { useTheme } from "../../../../app/providers/ThemeProvider";
import { useMobileMenu } from "../../../../app/providers/MobileMenuProvider";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { ThemeToggle } from "../../ui/ThemeToggle";
import logoDark from "../../../../assets/images/Tencotics,logo-02.png";
import logoLight from "../../../../assets/images/Tencotics,logo-04.png";
import "./mobile-nav.css";

const NAV_LINKS = [
  { to: ROUTES.HOME, label: "Inicio" },
  { to: ROUTES.QUIENES_SOMOS, label: "Nosotros" },
  { to: ROUTES.SERVICIOS_TI, label: "Servicios TI" },
  { to: ROUTES.SERVICIOS_CLOUD, label: "Servicios Cloud" },
  { to: ROUTES.INFRAESTRUCTURA, label: "Infraestructura" },
  { to: ROUTES.DESARROLLO, label: "Desarrollo" },
  { to: ROUTES.PROYECTOS, label: "Proyectos" },
  { to: ROUTES.CONTACTO, label: "Contacto" },
] as const;

function isNavActive(pathname: string, to: string): boolean {
  if (to === ROUTES.HOME) return pathname === ROUTES.HOME;
  return pathname === to || pathname.startsWith(`${to}/`);
}

const Navbar = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const { isOpen: menuOpen, closeMenu, toggleMenu } = useMobileMenu();

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    if (!isMobile || !menuOpen) return;

    const html = document.documentElement;
    const { style: bodyStyle } = document.body;
    const scrollY = window.scrollY;
    const prevBody = {
      position: bodyStyle.position,
      top: bodyStyle.top,
      left: bodyStyle.left,
      right: bodyStyle.right,
      width: bodyStyle.width,
      overflow: bodyStyle.overflow,
    };

    html.classList.add("tec-public-menu-open");
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.left = "0";
    bodyStyle.right = "0";
    bodyStyle.width = "100%";
    bodyStyle.overflow = "hidden";

    return () => {
      html.classList.remove("tec-public-menu-open");
      bodyStyle.position = prevBody.position;
      bodyStyle.top = prevBody.top;
      bodyStyle.left = prevBody.left;
      bodyStyle.right = prevBody.right;
      bodyStyle.width = prevBody.width;
      bodyStyle.overflow = prevBody.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [isMobile, menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen, closeMenu]);

  const handleNavClick = (to: string) => {
    if (to === ROUTES.HOME && location.pathname === ROUTES.HOME) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    closeMenu();
  };

  return (
    <>
      <header className="app-chrome public-header">
        <Link to={ROUTES.HOME} className="public-header__brand">
          <img
            src={theme === "dark" ? logoDark : logoLight}
            alt="Tecnotics"
            className="public-header__brand-logo"
          />
        </Link>

        <div className="public-header__right">
          {!isMobile && (
            <nav className="public-header__nav" aria-label="Navegación principal">
              {NAV_LINKS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="app-nav-link"
                  onClick={() => handleNavClick(item.to)}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          )}
          <ThemeToggle />
          {!isMobile && (
            <a
              href={ROUTES.LOGIN_EXTERNO}
              className="btn-chrome btn-chrome--primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Login
            </a>
          )}
          {isMobile && (
            <button
              type="button"
              className={`public-header__menu-btn ${menuOpen ? "public-header__menu-btn--open" : ""}`}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-panel"
              aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
              onClick={toggleMenu}
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} />
            </button>
          )}
        </div>
      </header>

      {isMobile && (
        <>
          <button
            type="button"
            className={`mobile-nav__backdrop ${menuOpen ? "mobile-nav__backdrop--open" : ""}`}
            aria-label="Cerrar menú"
            tabIndex={menuOpen ? 0 : -1}
            onClick={closeMenu}
          />
          <aside
            id="mobile-nav-panel"
            className={`mobile-nav__panel ${menuOpen ? "mobile-nav__panel--open" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-hidden={!menuOpen}
            aria-label="Menú de navegación"
          >
            <div className="mobile-nav__header">
              <p className="mobile-nav__title">Menú</p>
              <button
                type="button"
                className="mobile-nav__close"
                aria-label="Cerrar menú"
                onClick={closeMenu}
              >
                <i className="ri-close-line" aria-hidden="true" />
              </button>
            </div>

            <nav className="mobile-nav__links" aria-label="Navegación principal">
              {NAV_LINKS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`mobile-nav__link ${isNavActive(location.pathname, item.to) ? "mobile-nav__link--active" : ""}`}
                  onClick={() => handleNavClick(item.to)}
                  tabIndex={menuOpen ? 0 : -1}
                >
                  <span className="mobile-nav__link-label">{item.label}</span>
                  <i
                    className="ri-arrow-right-s-line mobile-nav__link-icon"
                    aria-hidden="true"
                  />
                </Link>
              ))}
            </nav>

            <div className="mobile-nav__footer">
              <a
                href={ROUTES.LOGIN_EXTERNO}
                className="mobile-nav__cta"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                tabIndex={menuOpen ? 0 : -1}
              >
                <i className="ri-login-box-line" aria-hidden="true" />
                Iniciar sesión
              </a>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default Navbar;
